import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { listBondTypes, type BondCategory, type BondType } from '@peruselab/api-client/bondTypes';
import { Icon } from '../../components/Icon';
import { PublicLayout } from './PublicLayout';
import { CatalogueMeta } from './CatalogueMeta';
import { bondTermHead, GLOSSARY_PATH, glossaryHead } from './scientificMeta';

const CATEGORIES: readonly BondCategory[] = ['PRIMARY', 'NON_CLASSICAL', 'INTERMOLECULAR', 'SPECIALIZED'];

type Load =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly types: readonly BondType[] }
  | { readonly status: 'error' };

/** The 44 kinds of bond, read once per page: the glossary is small reference text. */
function useBondTypes(): Load {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    listBondTypes()
      .then((types) => {
        if (!cancelled) setLoad({ status: 'ready', types });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return load;
}

/**
 * ADR 0023 D7 (#1126): the public bond glossary -- every kind of bond the taxonomy knows, by
 * category, open to everyone, and readable by search engines (the server writes the same page into
 * the HTML, GlossaryPage).
 */
export function BondGlossaryPage() {
  const { t } = useTranslation();
  const load = useBondTypes();
  return (
    <PublicLayout>
      <article className="bond-glossary" aria-labelledby="bond-glossary-title">
        <h1 id="bond-glossary-title">{t('glossary.name')}</h1>
        <p className="bond-glossary-intro">{t('glossary.intro')}</p>
        {load.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
        {load.status === 'error' ? (
          <p className="import-error" role="alert">
            {t('glossary.loadError')}
          </p>
        ) : null}
        {load.status === 'ready' ? (
          <>
            <CatalogueMeta head={glossaryHead(t, load.types)} />
            {CATEGORIES.map((category) => {
              const inCategory = load.types.filter((type) => type.category === category);
              if (inCategory.length === 0) return null;
              return (
                <section key={category} aria-labelledby={`bond-category-${category}`}>
                  <h2 id={`bond-category-${category}`}>{t(`glossary.category.${category}`)}</h2>
                  <ul className="bond-glossary-list">
                    {inCategory.map((type) => (
                      <li key={type.slug}>
                        <Link to={`${GLOSSARY_PATH}/${type.slug}`}>{type.name}</Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        ) : null}
      </article>
    </PublicLayout>
  );
}

/** #1126: one kind of bond -- what it is, examples, how to draw it here, and its neighbours. */
export function BondTermPage() {
  const { t } = useTranslation();
  const { slug = '' } = useParams<{ slug: string }>();
  const load = useBondTypes();
  const type = load.status === 'ready' ? load.types.find((candidate) => candidate.slug === slug) : undefined;
  const related =
    load.status === 'ready' && type
      ? load.types.filter((other) => other.category === type.category && other.slug !== type.slug)
      : [];
  return (
    <PublicLayout>
      <Link to={GLOSSARY_PATH} className="public-back">
        <Icon name="arrowBack" />
        <span>{t('glossary.back')}</span>
      </Link>
      {load.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
      {load.status === 'error' ? (
        <p className="import-error" role="alert">
          {t('glossary.loadError')}
        </p>
      ) : null}
      {load.status === 'ready' && !type ? (
        <p className="import-error" role="alert">
          {t('glossary.notFound')}
        </p>
      ) : null}
      {type ? (
        <article className="bond-term" aria-labelledby="bond-term-title">
          <CatalogueMeta head={bondTermHead(t, type)} />
          <h1 id="bond-term-title">{type.name}</h1>
          <p className="bond-term-category">{t(`glossary.category.${type.category}`)}</p>
          <p>{type.definition}</p>
          {type.examples.length > 0 ? (
            <section aria-labelledby="bond-term-examples">
              <h2 id="bond-term-examples">{t('glossary.examples')}</h2>
              <ul>
                {type.examples.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {type.drawing ? (
            <section aria-labelledby="bond-term-drawing" className="bond-term-drawing">
              <h2 id="bond-term-drawing">{t('glossary.howToDraw')}</h2>
              <p>
                <strong className={`bond-term-how bond-term-how--${type.drawing.how.toLowerCase()}`}>
                  {t(`glossary.how.${type.drawing.how}`)}
                </strong>{' '}
                {type.drawing.note}
              </p>
            </section>
          ) : null}
          {related.length > 0 ? (
            <section aria-labelledby="bond-term-related">
              <h2 id="bond-term-related">{t(`glossary.category.${type.category}`)}</h2>
              <ul className="bond-glossary-list">
                {related.map((other) => (
                  <li key={other.slug}>
                    <Link to={`${GLOSSARY_PATH}/${other.slug}`}>{other.name}</Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </article>
      ) : null}
    </PublicLayout>
  );
}
